import React, { useCallback, useMemo } from 'react';
import classNameBind from 'classnames/bind';
import moment from 'moment';
import Popover from '../Popover';
import { useBlur } from '../../hooks/useBlur';
import styles from './DatePicker.module.css';
import CalendarDatePicker from './components/CalendarDatePicker/CalendarDatePicker';
import getDateInAllowedRange from './methods/getDateInAllowedRange';
import { PickerButton } from '../PickerButton';

const FIRST_DAY_OF_THE_WEEK = 1;
const bindClasses = classNameBind.bind(styles);

const WithState = WrappedComponent => (
  class DatePickerWithState extends React.Component {
    static defaultProps = {
      min: moment(31532400000),
      // 2104-12-31
      max: moment(4260121200000),
    }

    constructor(props) {
      super(props);

      this.state = {
        value: props.value,
      };

      this.handleChange = this.handleChange.bind(this);
    }

    getValidDate(date) {
      const { min, max } = this.props;

      return getDateInAllowedRange(date, min, max);
    }

    handleChange(event) {
      const { onChange } = this.props;

      this.setState({
        value: this.getValidDate(event.target.value),
      }, () => {
        onChange(event);
      });
    }

    render() {
      const { value } = this.state;

      return <WrappedComponent {...this.props} value={value} onChange={this.handleChange} />;
    }
  }
);

const CalendarDatePickerWrapper = ({
  min, max, value, onChange, reportDatePickerLikeYearsRange,
}) => (
  <div className={bindClasses('popover-layout')}>
    <div className={bindClasses('calendar')}>
      <CalendarDatePicker
        selectedDate={value}
        min={min}
        max={max}
        firstDayOfWeek={FIRST_DAY_OF_THE_WEEK}
        onChange={onChange}
        reportDatePickerLikeYearsRange={reportDatePickerLikeYearsRange}
      />
    </div>
  </div>
);

const DatePicker = ({
  name,
  onBlur,
  min,
  max,
  dataId,
  disabled,
  error,
  touched,
  value,
  onChange,
  t,
  reportDatePickerLikeYearsRange,
} = {}) => {
  const { ref, handleBlur } = useBlur({ name, value, onBlur, disabled });
  const label = useMemo(() => {
    const labelGenerator = start => t('date.long', { date: start.toDate() });
    const datePickerLabel = value ? labelGenerator(value) : t('selectDate');

    return <>{datePickerLabel}</>;
  }, [value, t]);
  const handleChange = useCallback((selectedDate) => {
    onChange({
      target: {
        name,
        value: selectedDate,
      },
    });
  }, [onChange, name]);
  const invalid = touched && Boolean(error);

  return (
    <div
      className={bindClasses('wrapper')}
      data-ppuc-id={dataId}
      ref={ref}
    >
      <Popover
        activatorElement={(
          <PickerButton
            icon={{ type: 'icon', content: 'calendar' }}
            dataId={dataId}
            disabled={disabled}
            invalid={invalid}
          >
            {label}
          </PickerButton>
        )}
        subdued
        speechBubble
        position="bottom"
        disabled={disabled}
        noPadding
        childToggle={{ onChange: () => true }}
        onBlur={handleBlur}
        dataId={`${dataId}.popover`}
      >
        <CalendarDatePickerWrapper
          selectedDate={value}
          min={min}
          max={max}
          firstDayOfWeek={FIRST_DAY_OF_THE_WEEK}
          onChange={handleChange}
          reportDatePickerLikeYearsRange={reportDatePickerLikeYearsRange}
        />
      </Popover>
    </div>
  );
};

DatePicker.defaultProps = {
  value: null,
  /* Default values are based on ClickHouse date support used in Analytics
     (PPNA-361) - updated by comparison */
  min: moment(31532400000), // 1971-01-01
  max: moment(4260121200000), // 2104-12-31
  dataId: 'DatePicker',
  disabled: false,
  error: null,
  touched: false,
  onBlur: () => {},
  reportDatePickerLikeYearsRange: false,
};

const StatefulDatePicker = WithState(DatePicker);
StatefulDatePicker.displayName = 'DatePicker';

export {
  StatefulDatePicker,
  DatePicker,
};
